import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import type { ServerCredentialInfo } from '@/api/type-server'
import CopyIcon from '@/assets/icons/copy.svg?react'
import { message } from '@/components'
import { Button } from '@/components/base/Button'
import { Modal } from '@/components/base/Modal'

interface RotateCredentialModalProps {
  item: ServerCredentialInfo
  onClose: () => void
  onRotated: () => void
}

export function RotateCredentialModal({ item, onClose, onRotated }: RotateCredentialModalProps) {
  const { t } = useTranslation('accessKeys')
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<{ ak: string; sk: string } | null>(null)

  const handleConfirm = async () => {
    setLoading(true)
    try {
      const res = await API.rotateCredential({ res_id: item.resID })
      if (res.code !== 0) return
      setResult({ ak: res.accessKeyEncoded, sk: res.secretKeyEncoded })
      onRotated()
    } finally {
      setLoading(false)
    }
  }

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text)
    message.success(t('copied'))
  }

  if (result) {
    return (
      <Modal className="w-[640px]" open={true} onClose={onClose}>
        <Modal.Header>{t('rotateModal.title')}</Modal.Header>
        <Modal.Body>
          <p className="t-sm text-secondary mb-4">
            {t('rotateModal.warning')}
            <span className="text-highlight">{t('rotateModal.warningHighlight')}</span>
          </p>
          <div className="flex flex-col gap-2">
            <div>
              <div className="t-sb text-primary mb-2">{t('rotateModal.ak')}</div>
              <div className="flex items-center gap-2 border border-gray100 rounded-lg px-4 py-3">
                <span className="flex-1 break-all t-sm text-primary">{result.ak}</span>
                <CopyIcon
                  className="w-5 h-5 text-tertiary cursor-pointer shrink-0 hover:text-secondary"
                  onClick={() => handleCopy(result.ak)}
                />
              </div>
            </div>
            <div>
              <div className="t-sb text-primary mb-2">{t('rotateModal.sk')}</div>
              <div className="flex items-center gap-2 border border-gray100 rounded-lg px-4 py-3">
                <span className="flex-1 break-all t-sm text-primary">{result.sk}</span>
                <CopyIcon
                  className="w-5 h-5 text-tertiary cursor-pointer shrink-0 hover:text-secondary"
                  onClick={() => handleCopy(result.sk)}
                />
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button type="secondary" onClick={onClose} className="min-w-[88px]">
            {t('rotateModal.okBtn')}
          </Button>
        </Modal.Footer>
      </Modal>
    )
  }

  return (
    <Modal className="w-[640px]" open={true} onClose={onClose}>
      <Modal.Header>{t('rotateModal.title')}</Modal.Header>
      <Modal.Body>
        <p className="t-lg text-secondary mb-4">{t('rotateModal.confirm')}</p>
        <div className="bg-error/10 border border-error/40 rounded-lg p-4">
          <p className="t-sm text-secondary">
            {t('rotateModal.revokeWarning')}
            <span className="text-error font-bold">{t('rotateModal.permanentlyRevoke')}</span>
            {t('rotateModal.revokeFollowing')}
          </p>
          <ul className="list-disc list-inside mt-2 t-sm text-secondary pl-2">
            <li>
              {t('rotateModal.accessKeyId')}
              {item.title}
            </li>
            <li>{t('rotateModal.existingSecretKey')}</li>
          </ul>
          <p className="t-sm text-secondary mt-3">
            {t('rotateModal.permanentWarning')}
            <span className="text-error font-bold">{t('rotateModal.cannotBeUndone')}</span>
            {t('rotateModal.permanentWarningAfter')}
          </p>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button type="outline" onClick={onClose} className="min-w-[88px]">
          {t('rotateModal.cancelBtn')}
        </Button>
        <Button type="danger" onClick={handleConfirm} disabled={loading} className="min-w-[88px]">
          {t('rotateModal.confirmBtn')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
